import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';

const Configuration = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    prestamos: {
      tasaInteresMensual: 2.5,
      tasaInteresMinima: 0.5,
      tasaInteresMaxima: 10,
      montoMinimo: 100,
      montoMaximo: 50000,
      plazoMinimo: 1,
      plazoMaximo: 60,
      mesesEsperaParaPrestamo: 3
    },
    sistema: {
      nombreEmpresa: 'COPFAMI',
      moneda: 'USD',
      simboloMoneda: '$',
      formatoFecha: 'DD/MM/YYYY',
      zonaHoraria: 'America/Guayaquil'
    },
    notificaciones: {
      emailNotificaciones: true,
      recordatoriosPago: true,
      diasAnticipacionRecordatorio: 5
    }
  });

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/configuracion');
      setConfig(response.data);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      // Si no existe configuración, usar valores por defecto
      if (error.response?.status === 404) {
        toast.info('Usando configuración por defecto');
      } else {
        toast.error('Error al cargar la configuración');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async () => {
    try {
      setSaving(true);
      await axios.put('/api/configuracion', config);
      toast.success('Configuración guardada exitosamente');
      fetchConfiguration();
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      toast.error(error.response?.data?.message || 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateConfig = (seccion, campo, valor) => {
    setConfig(prev => ({
      ...prev,
      [seccion]: {
        ...prev[seccion],
        [campo]: valor
      }
    }));
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
        <p className="mt-3">Cargando configuración...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="text-gradient mb-2">
                      <i className="fas fa-cog me-2"></i>
                      Configuración del Sistema
                    </h2>
                    <p className="text-muted mb-0">
                      Configure los parámetros generales del sistema
                    </p>
                  </div>
                  <Button 
                    variant="success" 
                    onClick={handleSaveConfiguration}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Guardar Configuración
                      </>
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Configuración de Préstamos */}
          <Col lg={6}>
            <Card className="card-custom mb-4">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-hand-holding-usd me-2"></i>
                  Parámetros de Préstamos
                </h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Tasa de Interés Mensual (%)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.1"
                      min="0"
                      value={config.prestamos.tasaInteresMensual}
                      onChange={(e) => handleUpdateConfig('prestamos', 'tasaInteresMensual', parseFloat(e.target.value))}
                    />
                    <InputGroup.Text>% mensual</InputGroup.Text>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Tasa de interés mensual que se aplicará a los préstamos
                  </Form.Text>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tasa Mínima (%)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        value={config.prestamos.tasaInteresMinima}
                        onChange={(e) => handleUpdateConfig('prestamos', 'tasaInteresMinima', parseFloat(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tasa Máxima (%)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        value={config.prestamos.tasaInteresMaxima}
                        onChange={(e) => handleUpdateConfig('prestamos', 'tasaInteresMaxima', parseFloat(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <hr />

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Monto Mínimo (USD)</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control
                          type="number"
                          min="0"
                          value={config.prestamos.montoMinimo}
                          onChange={(e) => handleUpdateConfig('prestamos', 'montoMinimo', parseFloat(e.target.value))}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Monto Máximo (USD)</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control
                          type="number"
                          min="0"
                          value={config.prestamos.montoMaximo}
                          onChange={(e) => handleUpdateConfig('prestamos', 'montoMaximo', parseFloat(e.target.value))}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Plazo Mínimo (meses)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={config.prestamos.plazoMinimo}
                        onChange={(e) => handleUpdateConfig('prestamos', 'plazoMinimo', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Plazo Máximo (meses)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={config.prestamos.plazoMaximo}
                        onChange={(e) => handleUpdateConfig('prestamos', 'plazoMaximo', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Meses de Espera para Solicitar Préstamo</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="24"
                    value={config.prestamos.mesesEsperaParaPrestamo}
                    onChange={(e) => handleUpdateConfig('prestamos', 'mesesEsperaParaPrestamo', parseInt(e.target.value))}
                  />
                  <Form.Text className="text-muted">
                    Meses que debe esperar un socio desde su ingreso para poder solicitar un préstamo
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          {/* Configuración del Sistema */}
          <Col lg={6}>
            <Card className="card-custom mb-4">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-building me-2"></i>
                  Información del Sistema
                </h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre de la Empresa</Form.Label>
                  <Form.Control
                    type="text"
                    value={config.sistema.nombreEmpresa}
                    onChange={(e) => handleUpdateConfig('sistema', 'nombreEmpresa', e.target.value)}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Moneda</Form.Label>
                      <Form.Select
                        value={config.sistema.moneda}
                        onChange={(e) => handleUpdateConfig('sistema', 'moneda', e.target.value)}
                      >
                        <option value="USD">Dólar Estadounidense (USD)</option>
                        <option value="EUR">Euro (EUR)</option>
                        <option value="COP">Peso Colombiano (COP)</option>
                        <option value="MXN">Peso Mexicano (MXN)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Símbolo de Moneda</Form.Label>
                      <Form.Control
                        type="text"
                        value={config.sistema.simboloMoneda}
                        onChange={(e) => handleUpdateConfig('sistema', 'simboloMoneda', e.target.value)}
                        maxLength="3"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Formato de Fecha</Form.Label>
                  <Form.Select
                    value={config.sistema.formatoFecha}
                    onChange={(e) => handleUpdateConfig('sistema', 'formatoFecha', e.target.value)}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Zona Horaria</Form.Label>
                  <Form.Select
                    value={config.sistema.zonaHoraria}
                    onChange={(e) => handleUpdateConfig('sistema', 'zonaHoraria', e.target.value)}
                  >
                    <option value="America/Guayaquil">Ecuador (GMT-5)</option>
                    <option value="America/Bogota">Colombia (GMT-5)</option>
                    <option value="America/Lima">Perú (GMT-5)</option>
                    <option value="America/Mexico_City">México (GMT-6)</option>
                    <option value="America/New_York">Nueva York (GMT-5)</option>
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>

            {/* Configuración de Notificaciones */}
            <Card className="card-custom mb-4">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-bell me-2"></i>
                  Notificaciones
                </h5>
              </Card.Header>
              <Card.Body>
                <Form.Check
                  type="checkbox"
                  label="Enviar notificaciones por email"
                  checked={config.notificaciones.emailNotificaciones}
                  onChange={(e) => handleUpdateConfig('notificaciones', 'emailNotificaciones', e.target.checked)}
                  className="mb-3"
                />

                <Form.Check
                  type="checkbox"
                  label="Recordatorios de pago"
                  checked={config.notificaciones.recordatoriosPago}
                  onChange={(e) => handleUpdateConfig('notificaciones', 'recordatoriosPago', e.target.checked)}
                  className="mb-3"
                />

                {config.notificaciones.recordatoriosPago && (
                  <Form.Group className="mb-3">
                    <Form.Label>Días de anticipación para recordatorios</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="30"
                      value={config.notificaciones.diasAnticipacionRecordatorio}
                      onChange={(e) => handleUpdateConfig('notificaciones', 'diasAnticipacionRecordatorio', parseInt(e.target.value))}
                    />
                    <Form.Text className="text-muted">
                      Días antes del vencimiento para enviar recordatorio
                    </Form.Text>
                  </Form.Group>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Información adicional */}
        <Row>
          <Col>
            <Alert variant="warning">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Importante:</strong> Los cambios en la configuración afectarán a todos los nuevos préstamos y operaciones del sistema. Los préstamos existentes mantendrán sus parámetros originales.
            </Alert>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default Configuration;
